'use client';

import { getNatsClient } from '@khal-os/sdk/app';
import { useNotificationStore } from '@khal-os/ui';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAppearance } from '../../hooks/useAppearance';

export type InstallPhase = 'idle' | 'started' | 'succeeded' | 'failed';

export interface InstallProgress {
	installId: string;
	phase: InstallPhase;
	message?: string;
}

export interface InstallFromUrlDialogProps {
	open: boolean;
	onClose: () => void;
	/**
	 * Called when the install transitions to a terminal phase. MarketplaceView
	 * uses this to render a phase string inside the marketplace card while the
	 * dialog itself is closed.
	 */
	onProgress?: (progress: InstallProgress) => void;
	/** Called after a successful install so the catalog can refresh. */
	onInstalled?: () => void;
}

interface InstallResponse {
	installId?: string;
	success?: boolean;
	error?: string;
}

function parseMaybeJson(raw: unknown): Record<string, unknown> {
	if (typeof raw === 'string') {
		try {
			return JSON.parse(raw) as Record<string, unknown>;
		} catch {
			return {};
		}
	}
	return (raw ?? {}) as Record<string, unknown>;
}

/** Keep the success message visible long enough for a human to read it. */
const SUCCESS_HOLD_MS = 2_500;

export function InstallFromUrlDialog({ open, onClose, onProgress, onInstalled }: InstallFromUrlDialogProps) {
	const [repoUrl, setRepoUrl] = useState('');
	const [submitting, setSubmitting] = useState(false);
	const [phase, setPhase] = useState<InstallPhase>('idle');
	const [errorMsg, setErrorMsg] = useState<string | null>(null);
	const [successMsg, setSuccessMsg] = useState<string | null>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	const addNotification = useNotificationStore((s) => s.addNotification);
	const { lightCallouts } = useAppearance();
	const successPanelLight = lightCallouts && phase === 'succeeded';

	const reset = () => {
		setRepoUrl('');
		setSubmitting(false);
		setPhase('idle');
		setErrorMsg(null);
		setSuccessMsg(null);
	};

	const handleClose = () => {
		if (submitting) return;
		reset();
		onClose();
	};

	useEffect(() => {
		if (!open) return;
		const handler = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				e.preventDefault();
				handleClose();
			}
		};
		window.addEventListener('keydown', handler, { capture: true });
		return () => window.removeEventListener('keydown', handler, { capture: true });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, submitting]);

	useEffect(() => {
		if (open) inputRef.current?.focus();
	}, [open]);

	const trimmedUrl = repoUrl.trim();
	const canSubmit = useMemo(() => trimmedUrl.length > 0 && !submitting, [trimmedUrl, submitting]);

	async function handleSubmit(e?: React.FormEvent) {
		e?.preventDefault();
		if (!canSubmit) return;
		setSubmitting(true);
		setErrorMsg(null);
		setPhase('started');

		const client = getNatsClient();
		let settled = false;

		const finish = (success: boolean, installId: string, message?: string) => {
			if (settled) return;
			settled = true;
			console.info('[InstallFromUrlDialog] finish', { success, message });
			if (success) {
				addNotification({
					summary: 'App installed',
					body: message ?? trimmedUrl,
					icon: null,
					actions: [],
					expires: 4000,
					id: Date.now(),
					replacesId: 0,
				});
				setPhase('succeeded');
				setSuccessMsg(message ?? 'Install complete.');
				onProgress?.({ installId, phase: 'succeeded', message });
				setTimeout(() => {
					onInstalled?.();
					reset();
					onClose();
				}, SUCCESS_HOLD_MS);
			} else {
				setSubmitting(false);
				setPhase('failed');
				setErrorMsg(message ?? 'Install failed');
				onProgress?.({ installId, phase: 'failed', message });
			}
		};

		try {
			console.info('[InstallFromUrlDialog] submitting', { repoUrl: trimmedUrl });
			const raw = await client.request('os.marketplace.install', { repoUrl: trimmedUrl }, 15000);
			const data = parseMaybeJson(raw) as InstallResponse;
			console.info('[InstallFromUrlDialog] response', data);
			const installId = data.installId ?? '';
			const hasUsefulField = data.installId !== undefined || data.success !== undefined || data.error !== undefined;

			if (data.success === true) {
				finish(true, installId);
			} else if (data.success === false || data.error) {
				finish(false, installId, data.error ?? 'Install failed');
			} else if (!hasUsefulField) {
				finish(false, installId, 'Install service returned an empty response — is the marketplace handler running?');
			} else {
				// Has installId but no explicit success/error — treat as missing signal.
				finish(false, installId, 'Install service returned no success signal');
			}
		} catch (err) {
			finish(false, '', err instanceof Error ? err.message : String(err));
		}
	}

	if (!open) return null;

	return (
		<div
			className="k-dialog-backdrop fixed inset-0 z-[10000]"
			onClick={(e) => {
				if (e.target === e.currentTarget) handleClose();
			}}
			onKeyDown={undefined}
			role="presentation"
			data-testid="install-dialog-backdrop"
		>
			<form
				onSubmit={handleSubmit}
				className={`k-dialog${successPanelLight ? ' khal-light' : ''}`}
				role="dialog"
				aria-modal="true"
				aria-label="Install from GitHub URL"
				style={{ maxWidth: 520 }}
			>
				<div className="k-dialog-h">
					<h3>Install from GitHub URL</h3>
					<p>Paste a GitHub repository URL. The marketplace will validate and deploy the pack.</p>
				</div>

				<div className="k-dialog-body">
					<input
						ref={inputRef}
						type="url"
						value={repoUrl}
						onChange={(e) => setRepoUrl(e.target.value)}
						placeholder="https://github.com/owner/repo"
						aria-label="GitHub repository URL"
						disabled={submitting}
						className="k-input"
						style={{ display: 'flex', width: '100%' }}
					/>

					{phase === 'started' && (
						<p className="k-helper" style={{ marginTop: 12, color: 'var(--khal-muted)' }}>
							Installing…
						</p>
					)}

					{successMsg && (
						<p
							className="k-helper"
							role="status"
							style={{ marginTop: 12, color: 'var(--khal-status-live)', fontWeight: 500 }}
						>
							✓ {successMsg}
						</p>
					)}

					{errorMsg && (
						<p
							className="k-helper"
							role="alert"
							style={{ marginTop: 12, color: 'var(--khal-error)', fontWeight: 500 }}
						>
							⚠ {errorMsg}
						</p>
					)}
				</div>

				<div className="k-dialog-foot">
					<button
						type="button"
						onClick={handleClose}
						disabled={submitting}
						className="k-btn k-btn-ghost k-btn-sm"
					>
						Cancel
					</button>
					<button
						type="submit"
						disabled={!canSubmit}
						className="k-btn k-btn-primary k-btn-sm"
					>
						{submitting ? 'Installing…' : 'Install'}
					</button>
				</div>
			</form>
		</div>
	);
}
